import React, { FC, useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Select, Switch, Button, Typography, Divider, Radio } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useNewIntegrationsStore } from '@/store/newIntegration';

const { Text } = Typography;
const { Option } = Select;

interface ListConfigModalProps {
  visible: boolean;
  onClose: () => void;
  listPath: string;
}

interface ListConfig {
  type: 'simple' | 'pagination' | 'batch';
  iterationPath: string;
  batchSize?: number;
  paginationConfig?: {
    type: 'offset' | 'page' | 'cursor';
    limitParam?: string;
    offsetParam?: string;
    pageParam?: string;
    cursorParam?: string;
    responsePathToNextCursor?: string;
    responsePathToItems?: string;
  };
  outputConfig: {
    strategy: 'individual' | 'bulk' | 'aggregate';
    aggregateField?: string;
  };
}

const DEFAULT_CONFIG: ListConfig = {
  type: 'simple',
  iterationPath: '',
  batchSize: 100,
  paginationConfig: {
    type: 'offset',
    limitParam: 'limit',
    offsetParam: 'offset',
    pageParam: 'page',
    cursorParam: 'cursor',
    responsePathToNextCursor: 'next_cursor',
    responsePathToItems: 'items'
  },
  outputConfig: {
    strategy: 'individual',
    aggregateField: 'items'
  }
};

const ListConfigModal: FC<ListConfigModalProps> = ({ visible, onClose, listPath }) => {
  const [form] = Form.useForm();
  const [config, setConfig] = useState<ListConfig>({ ...DEFAULT_CONFIG, iterationPath: listPath });
  const newIntegration = useNewIntegrationsStore(state => state.newIntegration);
  const setNewIntegration = useNewIntegrationsStore(state => state.setNewIntegration);

  // Update form when the modal becomes visible or listPath changes
  useEffect(() => {
    if (visible) {
      // Load existing config if available
      const existingConfig = newIntegration?.source?.listConfig;
      if (existingConfig) {
        setConfig(existingConfig);
        form.setFieldsValue(existingConfig);
      } else {
        // Set default config with the current list path
        const defaultConfig = { ...DEFAULT_CONFIG, iterationPath: listPath };
        setConfig(defaultConfig);
        form.setFieldsValue(defaultConfig);
      }
    }
  }, [visible, listPath, form, newIntegration]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      // Update the config
      const updatedConfig = {
        ...config,
        ...values,
      };
      
      // Save to the integration
      if (newIntegration) {
        setNewIntegration({
          ...newIntegration,
          source: {
            ...newIntegration.source,
            listConfig: updatedConfig
          }
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title="Configure List Processing"
      open={visible}
      width={600}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={config}
      >
        <Text type="secondary">
          Configure how the system should process list data from the source.
        </Text>
        
        <Divider plain>List Source</Divider>
        
        <Form.Item
          name="iterationPath"
          label="Path to list in source data"
          tooltip="The path to the array in the source payload. Leave empty if the payload itself is an array."
          rules={[{ required: false }]}
        >
          <Input placeholder="e.g., data.items or leave empty for root array" />
        </Form.Item>
        
        <Form.Item 
          name="type" 
          label="List Processing Type"
          tooltip="How the system should handle the list data from the source"
        >
          <Radio.Group>
            <Radio value="simple">Simple (Process whole list at once)</Radio>
            <Radio value="batch">Batch Processing (Process in chunks)</Radio>
            <Radio value="pagination">API Pagination (Handle paginated results)</Radio>
          </Radio.Group>
        </Form.Item>
        
        <Form.Item 
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
        >
          {({ getFieldValue }) => {
            const listType = getFieldValue('type');
            
            return listType === 'batch' ? (
              <Form.Item
                name="batchSize"
                label="Batch Size"
                tooltip="Number of items to process in each batch"
                rules={[{ required: true, message: 'Please input the batch size!' }]}
              >
                <InputNumber min={1} max={1000} />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
        
        <Form.Item 
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
        >
          {({ getFieldValue }) => {
            const listType = getFieldValue('type');
            
            return listType === 'pagination' ? (
              <>
                <Form.Item
                  name={['paginationConfig', 'type']}
                  label="Pagination Type"
                  tooltip="The type of pagination used by the source API"
                >
                  <Select>
                    <Option value="offset">Offset-based (e.g., limit/offset)</Option>
                    <Option value="page">Page-based (e.g., page/size)</Option>
                    <Option value="cursor">Cursor-based (e.g., next_cursor)</Option>
                  </Select>
                </Form.Item>
                
                <Form.Item 
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => 
                    prevValues.paginationConfig?.type !== currentValues.paginationConfig?.type}
                >
                  {({ getFieldValue }) => {
                    const paginationType = getFieldValue(['paginationConfig', 'type']);
                    
                    if (paginationType === 'offset') {
                      return (
                        <>
                          <Form.Item
                            name={['paginationConfig', 'limitParam']}
                            label="Limit Parameter Name"
                          >
                            <Input placeholder="limit" />
                          </Form.Item>
                          <Form.Item
                            name={['paginationConfig', 'offsetParam']}
                            label="Offset Parameter Name"
                          >
                            <Input placeholder="offset" />
                          </Form.Item>
                        </>
                      );
                    }
                    
                    if (paginationType === 'page') {
                      return (
                        <>
                          <Form.Item
                            name={['paginationConfig', 'pageParam']}
                            label="Page Parameter Name"
                          >
                            <Input placeholder="page" />
                          </Form.Item>
                          <Form.Item
                            name={['paginationConfig', 'limitParam']}
                            label="Page Size Parameter Name"
                          >
                            <Input placeholder="page_size" />
                          </Form.Item>
                        </>
                      );
                    }
                    
                    if (paginationType === 'cursor') {
                      return (
                        <>
                          <Form.Item
                            name={['paginationConfig', 'cursorParam']}
                            label="Cursor Parameter Name"
                          >
                            <Input placeholder="cursor" />
                          </Form.Item>
                          <Form.Item
                            name={['paginationConfig', 'responsePathToNextCursor']}
                            label="Path to Next Cursor in Response"
                          >
                            <Input placeholder="meta.next_cursor" />
                          </Form.Item>
                        </>
                      );
                    }
                    
                    return null;
                  }}
                </Form.Item>
                
                <Form.Item
                  name={['paginationConfig', 'responsePathToItems']}
                  label="Path to Items in Paginated Response"
                  tooltip="The path to find the array of items in each paginated response"
                >
                  <Input placeholder="data.items" />
                </Form.Item>
              </>
            ) : null;
          }}
        </Form.Item>
        
        <Divider plain>Output Configuration</Divider>
        
        <Form.Item
          name={['outputConfig', 'strategy']}
          label="Output Strategy"
          tooltip="How to handle multiple items when sending to the destination"
        >
          <Select>
            <Option value="individual">Individual (Process each item separately)</Option>
            <Option value="bulk">Bulk (Send all items in one request)</Option>
            <Option value="aggregate">Aggregate (Combine items into a single object)</Option>
          </Select>
        </Form.Item>
        
        <Form.Item 
          noStyle
          shouldUpdate={(prevValues, currentValues) => 
            prevValues.outputConfig?.strategy !== currentValues.outputConfig?.strategy}
        >
          {({ getFieldValue }) => {
            const strategy = getFieldValue(['outputConfig', 'strategy']);
            
            return strategy === 'aggregate' ? (
              <Form.Item
                name={['outputConfig', 'aggregateField']}
                label="Field Name for Aggregated Items"
                tooltip="The field name to use when aggregating items into a single object"
              >
                <Input placeholder="items" />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ListConfigModal; 